// src/services/matchService.js
import { supabase } from '../supabaseClient'

/**
 * Sauvegarde un match (local + cloud si Internet disponible)
 */
export const saveMatch = async (match) => {
  console.log('💾 Sauvegarde du match...', match.id)
  
  const matches = JSON.parse(localStorage.getItem('matches') || '[]')
  
  const matchWithSync = {
    ...match,
    synced: false,
    needsSync: true,
    lastModified: new Date().toISOString()
  }
  
  // Upsert : remplacer si existe déjà, sinon ajouter
  const existingIndex = matches.findIndex(m => 
    m.matchId === match.matchId || m.id === match.id
  );
  if (existingIndex !== -1) {
    matches[existingIndex] = matchWithSync;
  } else {
    matches.push(matchWithSync);
  }
  localStorage.setItem('matches', JSON.stringify(matches))
  console.log('✅ Match sauvegardé en local')
  
  if (!navigator.onLine) {
    console.log('⚠️ Pas de connexion Internet, sauvegarde locale uniquement')
    return { success: true, offline: true, data: matchWithSync }
  }
  
  try {
    const matchData = prepareMatchForSupabase(match, false);
    
    const { data, error } = await supabase
      .from('matches')
      .insert(matchData)
      .select()
      .single()
    
    if (error) throw error
    
    console.log('✅ Match sauvegardé dans Supabase', data.id)
    
    const updatedMatches = matches.map(m => {
      if (m.matchId === match.matchId || m.id === match.id) {
        return {
          ...m,
          id: data.id,
          synced: true,
          needsSync: false,
          supabaseId: data.id
        };
      }
      return m;
    });
    
    localStorage.setItem('matches', JSON.stringify(updatedMatches))
    
    const updatedMatch = {
      ...match,
      id: data.id,
      synced: true,
      needsSync: false
    };
    
    localStorage.setItem(`match_${data.id}`, JSON.stringify(updatedMatch));
    
    if (match.matchId && match.matchId !== data.id) {
      localStorage.setItem(`match_${match.matchId}`, JSON.stringify(updatedMatch));
    }
    
    console.log('✅ Match mis à jour en local avec UUID:', data.id);
    
    return { success: true, online: true, data: updatedMatch }
    
  } catch (error) {
    console.error('❌ Erreur Supabase (sauvegarde locale OK):', error.message)
    return { success: true, offline: true, error: error.message }
  }
}

/**
 * Récupère tous les matchs (local + cloud fusionnés)
 */
export const getAllMatches = async () => {
  console.log('📂 Chargement des matchs...')
  
  // Récupérer l'utilisateur connecté
  const { data: { user } } = await supabase.auth.getUser();
  const currentUserId = user?.id;
  
  // 1. Charger locaux
  let localMatches = JSON.parse(localStorage.getItem('matches') || '[]')
  
  // Filtrer le localStorage par userId pour éviter les conflits entre comptes
  if (currentUserId) {
    const allLocalMatches = localMatches.length;
    localMatches = localMatches.filter(m => 
      m.userId === currentUserId || !m.userId
    );
    if (allLocalMatches !== localMatches.length) {
      console.log(`🔒 Filtrage local: ${allLocalMatches} → ${localMatches.length} matchs (userId: ${currentUserId.substring(0,8)}...)`);
    }
  }
  
  // Reconstruction si vide
  if (localMatches.length === 0) {
    console.log('⚠️ Liste vide, reconstruction...')
    
    const matchKeys = Object.keys(localStorage).filter(k => k.startsWith('match_'))
    matchKeys.forEach(key => {
      try {
        const matchData = JSON.parse(localStorage.getItem(key))
        if (currentUserId && matchData.userId && matchData.userId !== currentUserId) {
          return;
        }
        if (!matchData.id) {
          matchData.id = key.replace(/^match_/g, '')
        }
        if (!matchData.matchId) matchData.matchId = matchData.id
        matchData.needsSync = true
        matchData.synced = false
        localMatches.push(matchData)
      } catch (e) {
        console.error(`❌ ${key} corrompu`)
      }
    })
    
    if (localMatches.length > 0) {
      localStorage.setItem('matches', JSON.stringify(localMatches))
      console.log(`✅ ${localMatches.length} matchs reconstruits`)
    }
  }
  
  console.log(`📱 ${localMatches.length} matchs en local`)
  
  // 2. Si pas de connexion
  if (!navigator.onLine) {
    console.log('⚠️ Mode hors-ligne')
    return { matches: localMatches, offline: true }
  }
  
  // 3. Charger depuis Supabase (RLS filtre automatiquement par userId)
  try {
    const { data: cloudMatches, error } = await supabase
      .from('matches')
      .select('*')
      .order('"createdAt"', { ascending: false })
    
    if (error) throw error
    
    console.log(`☁️ ${cloudMatches?.length || 0} matchs dans le cloud`)
    
    let finalMatches = []
    
    if (cloudMatches && cloudMatches.length > 0) {
      const cloudMap = new Map(cloudMatches.map(m => [m.matchId || m.id, m]))
      const localMap = new Map(localMatches.map(m => [m.matchId || m.id, m]))
      const allIds = new Set([...cloudMap.keys(), ...localMap.keys()])
      
      allIds.forEach(id => {
        if (!id) {
          console.log(`  ⏭️ Skip null/undefined ID`)
          return
        }
        
        const cloudMatch = cloudMap.get(id)
        const localMatch = localMap.get(id)
        
        if (cloudMatch && localMatch) {
          const cloudDate = new Date(cloudMatch.updatedAt || cloudMatch.createdAt || 0)
          
          // 🆕 Lire le fichier individuel pour avoir les vraies données locales
          // (la liste globale peut être en retard sur le fichier individuel)
          const fileKey = `match_${id}`
          const fileData = localStorage.getItem(fileKey)
          const fileMatch = fileData ? JSON.parse(fileData) : localMatch
          
          // 🆕 Utiliser lastModified du fichier individuel pour la comparaison
          // (mis à jour après chaque point ET après chaque undo)
          const fileDate = new Date(fileMatch.lastModified || fileMatch.updatedAt || localMatch.updatedAt || localMatch.createdAt || 0)
          
          // 🛡️ Copie locale "propre" = déjà entièrement synchronisée (aucune modif en attente).
          // Dans ce cas le cloud fait foi : il contient tout ce que le local contient, et peut
          // avoir été corrigé ailleurs (fusion de joueurs par un admin, autre appareil...).
          const sm = fileMatch.syncMetadata
          const localClean = !!(sm && sm.matchLastSyncedAt) &&
            !(fileMatch.lastModified && fileMatch.lastModified > sm.matchLastSyncedAt)
          
          if (fileDate > cloudDate && !localClean) {
            const localPoints = fileMatch.engineState?.points?.length || 0
            const cloudPoints = cloudMatch.engineState?.points?.length || 0
            console.log(`  📱 Match ${id.substring(0,8)}... : Local plus récent (${localPoints} pts local vs ${cloudPoints} pts cloud)`)
            finalMatches.push({ ...fileMatch, needsSync: true })
          } else {
            console.log(`  ☁️ Match ${id.substring(0,8)}... : ${localClean ? 'Cloud fait foi (copie locale déjà synchronisée)' : 'Cloud à jour'}`)
            // On garde les métadonnées de synchro locales pour éviter un renvoi inutile
            finalMatches.push(localClean ? { ...cloudMatch, syncMetadata: sm } : cloudMatch)
          }
        } else if (cloudMatch) {
          console.log(`  ☁️ Match ${id.substring(0,8)}... : Cloud uniquement`)
          finalMatches.push(cloudMatch)
        } else if (localMatch) {
          console.log(`  📱 Match ${id.substring(0,8)}... : Local uniquement (à sync)`)
          finalMatches.push({ ...localMatch, needsSync: true })
        }
      })
      
      console.log(`✅ ${finalMatches.length} matchs chargés (merger local + cloud)`)
    } else if (localMatches.length > 0) {
      finalMatches = localMatches.map(m => ({ ...m, needsSync: true }))
      console.log(`⚠️ Cloud vide, conservation des ${localMatches.length} matchs locaux`)
    } else {
      finalMatches = []
      console.log(`✅ Aucun match`)
    }
    
    if (finalMatches.length > 0) {
      localStorage.setItem('matches', JSON.stringify(finalMatches))
      
      finalMatches.forEach(match => {
        const key = `match_${match.matchId || match.id}`
        const existing = localStorage.getItem(key)
        
        if (!existing) {
          localStorage.setItem(key, JSON.stringify(match))
        } else if (match.needsSync) {
          // Match local plus récent, garder tel quel
        } else {
          const localMatch = JSON.parse(existing)
          const localPoints = localMatch.engineState?.points?.length || 0
          const cloudPoints = match.engineState?.points?.length || 0
          
          if (cloudPoints >= localPoints) {
            localStorage.setItem(key, JSON.stringify(match))
          }
          // Sinon garder le local qui a plus de points
        }
      })
      console.log(`💾 ${finalMatches.length} fichiers individuels créés/mis à jour`)
    }
    
    return { matches: finalMatches, online: true }
    
  } catch (error) {
    console.error('❌ Erreur chargement Supabase:', error.message)
    return { matches: localMatches, offline: true, error: error.message }
  }
}

/**
 * Supprime un match (local + cloud)
 */
export const deleteMatch = async (matchId) => {
  console.log('🗑️ Suppression du match:', matchId)
  
  const matches = JSON.parse(localStorage.getItem('matches') || '[]')
  const updatedMatches = matches.filter(m => m.id !== matchId && m.matchId !== matchId)
  localStorage.setItem('matches', JSON.stringify(updatedMatches))
  
  localStorage.removeItem(`match_${matchId}`)
  
  console.log('✅ Match supprimé du localStorage')
  
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(matchId)
  
  if (!isUUID) {
    console.log('⚠️ ID numérique (ancien format), pas dans Supabase')
    return { success: true, localOnly: true }
  }
  
  if (!navigator.onLine) {
    console.log('⚠️ Pas de connexion, suppression locale uniquement')
    return { success: true, offline: true }
  }
  
  try {
    const { error: deleteError } = await supabase
      .from('matches')
      .delete()
      .eq('id', matchId)
    
    if (deleteError) {
      console.log('⚠️ Erreur Supabase:', deleteError.message)
      return { success: true, notFound: true }
    }
    
    console.log('✅ Match supprimé de Supabase:', matchId)
    return { success: true, online: true }
    
  } catch (error) {
    console.error('❌ Erreur suppression Supabase:', error.message)
    return { success: true, offline: true, error: error.message }
  }
}

/**
 * Synchronise les matchs en attente vers Supabase
 */
export const syncPendingMatches = async () => {
  console.log('🔄 Synchronisation des matchs en attente...')
  
  if (!navigator.onLine) {
    console.log('⚠️ Pas de connexion Internet')
    return { success: false, message: 'Pas de connexion Internet' }
  }
  
  const matches = JSON.parse(localStorage.getItem('matches') || '[]')
  const pending = matches.filter(m => m.needsSync)
  
  if (pending.length === 0) {
    console.log('✅ Aucun match à synchroniser')
    return { success: true, synced: 0 }
  }
  
  console.log(`📤 ${pending.length} matchs à synchroniser...`)
  
  let syncedCount = 0
  const updatedMatches = [...matches]
  
  for (const match of pending) {
    try {
      const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(match.id);
      
      if (isValidUUID) {
        const { data: existing } = await supabase
          .from('matches')
          .select('id')
          .eq('id', match.id)
          .maybeSingle();
        
        const matchData = prepareMatchForSupabase(match, true);
        
        if (existing) {
          console.log(`🔄 Mise à jour du match ${match.id} dans Supabase`);
          const { error } = await supabase
            .from('matches')
            .update(matchData)
            .eq('id', match.id);
          if (error) throw error;
        } else {
          console.log(`📤 Insertion du match ${match.id} dans Supabase`);
          const { error } = await supabase
            .from('matches')
            .insert(matchData);
          if (error) throw error;
        }
        
        const index = updatedMatches.findIndex(m => m.id === match.id);
        if (index !== -1) {
          updatedMatches[index].synced = true;
          updatedMatches[index].needsSync = false;
          updatedMatches[index].supabaseId = match.id;
        }
        
      } else {
        console.log(`🆕 Création d'un nouveau match (id invalide: ${match.id})`);
        
        const matchData = prepareMatchForSupabase(match, false);
        
        const { data: newMatch, error } = await supabase
          .from('matches')
          .insert(matchData)
          .select()
          .single();
        
        if (error) throw error;
        
        console.log(`✅ Match créé avec UUID: ${newMatch.id}`);
        
        const index = updatedMatches.findIndex(m => m.matchId === match.matchId || m.id === match.id);
        if (index !== -1) {
          updatedMatches[index].id = newMatch.id;
          updatedMatches[index].synced = true;
          updatedMatches[index].needsSync = false;
          updatedMatches[index].supabaseId = newMatch.id;
          localStorage.setItem(`match_${newMatch.id}`, JSON.stringify(updatedMatches[index]));
        }
      }
      
      syncedCount++
      console.log(`✅ Match ${match.id} synchronisé`)
      
    } catch (error) {
      console.error(`❌ Échec sync match ${match.id}:`, error.message)
    }
  }
  
  localStorage.setItem('matches', JSON.stringify(updatedMatches))
  console.log(`🎉 ${syncedCount}/${pending.length} matchs synchronisés`)
  return { success: true, synced: syncedCount, total: pending.length }
}

/**
 * Compte les matchs en attente de synchronisation
 */
export const getPendingMatchesCount = () => {
  const matches = JSON.parse(localStorage.getItem('matches') || '[]')
  return matches.filter(m => m.needsSync).length
}

/**
 * Active la synchronisation automatique au retour d'Internet
 */
let syncInProgress = false;

export const enableAutoSync = () => {
  window.addEventListener('online', async () => {
    if (syncInProgress) {
      console.log('⏸️ Synchronisation déjà en cours, ignore...');
      return;
    }
    
    console.log('🌐 Connexion Internet rétablie, synchronisation automatique...')
    syncInProgress = true;
    
    setTimeout(async () => {
      try {
        const result = await syncPendingMatches()
        if (result.synced > 0) {
          console.log(`✅ ${result.synced} match(s) synchronisé(s) automatiquement`)
        }
      } catch (error) {
        console.error('❌ Erreur auto-sync:', error)
      } finally {
        syncInProgress = false;
        setTimeout(() => { syncInProgress = false; }, 3000);
      }
    }, 2000)
  })
}

/**
 * Prépare un match pour l'insertion dans Supabase
 */
function prepareMatchForSupabase(match, isUpdate = false) {
  const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(match.id);
  
  const data = {
    ...(isUpdate && isValidUUID && { id: match.id }),
    userId: match.userId || null,
    // Liaison avec les fiches joueurs (n'écrase pas une liaison existante si absente)
    ...(match.player1Id && { player1Id: match.player1Id }),
    ...(match.player2Id && { player2Id: match.player2Id }),
    joueur1Nom: match.joueur1Nom || null,
    joueur2Nom: match.joueur2Nom || null,
    prenomJoueur1: match.prenomJoueur1 || null,
    prenomJoueur2: match.prenomJoueur2 || null,
    joueur1Club: match.joueur1Club || null,
    joueur2Club: match.joueur2Club || null,
    joueur1ClassementCode: match.joueur1ClassementCode || null,
    joueur2ClassementCode: match.joueur2ClassementCode || null,
    joueur1PaysCode: match.joueur1PaysCode || null,
    joueur2PaysCode: match.joueur2PaysCode || null,
    joueur1Droitier: match.joueur1Droitier || null,
    joueur2Droitier: match.joueur2Droitier || null,
    dateMatch: match.dateMatch || null,
    tournoiNom: match.tournoiNom || null,
    surfaceCode: match.surfaceCode || null,
    categorieCode: match.categorieCode || null,
    tourCode: match.tourCode || null,
    categorieageCode: match.categorieageCode || null,
    epreuveCode: match.epreuveCode || null,
    formatMatch: match.formatMatch || null,
    formatSet1: match.formatSet1 || match.formatSet1Code || null,
    formatSet2: match.formatSet2 || match.formatSet2Code || null,
    formatSet3: match.formatSet3 || match.formatSet3Code || null,
    formatSet4: match.formatSet4 || match.formatSet4Code || null,
    formatSet5: match.formatSet5 || match.formatSet5Code || null,
    toss: match.toss || null,
    premierServeur: match.premierServeur || null,
    vainqueur: match.vainqueur || null,
    currentScore: match.currentScore || null,
    engineState: match.engineState || null,
    matchComments: match.matchComments || null,
    status: match.status || 'FINISHED'
  };
  
  return data;
}

/**
 * Met à jour un match existant (local + cloud)
 */
export const updateMatch = async (match) => {
  console.log('🔄 Mise à jour du match...', match.id)
  
  // 1. Mettre à jour la liste globale
  const matches = JSON.parse(localStorage.getItem('matches') || '[]')
  const index = matches.findIndex(m => m.id === match.id || m.matchId === match.matchId)
  
  const nowTimestamp = new Date().toISOString()
  if (index !== -1) {
    matches[index] = {
      ...matches[index],
      ...match,
      lastModified: nowTimestamp,
      updatedAt: nowTimestamp,  // 🆕 Aussi mettre à jour updatedAt pour que getAllMatches le détecte
      needsSync: true,
      synced: false
    }
    localStorage.setItem('matches', JSON.stringify(matches))
    console.log('✅ Match mis à jour en local (liste globale)')
  }
  
  // 2. Mettre à jour le fichier individuel
  // 🆕 IMPORTANT : on préserve les points existants du localStorage
  // mais on écrase avec les nouvelles métadonnées (premierServeur, currentScore corrigé, etc.)
  const fileKey = `match_${match.matchId || match.id}`
  const savedData = JSON.parse(localStorage.getItem(fileKey) || '{}')
  const now = new Date().toISOString()
  const updatedFileData = {
    ...savedData,  // Préserver les données existantes (points, engineState, etc.)
    ...match,      // Écraser avec les nouvelles métadonnées
    // 🆕 Préserver les points et engineState du localStorage s'ils sont plus complets
    engineState: savedData.engineState?.points?.length > (match.engineState?.points?.length || 0)
      ? savedData.engineState
      : (match.engineState || savedData.engineState),
    pointsCount: savedData.pointsCount || 0,
    // 🆕 Mettre à jour lastModified pour que getAllMatches sache que le local est plus récent
    lastModified: now,
    updatedAt: now,
  }
  localStorage.setItem(fileKey, JSON.stringify(updatedFileData))
  console.log('✅ Match mis à jour en local (fichier individuel), server:', updatedFileData.currentScore?.server)
  
  if (!navigator.onLine) {
    console.log('⚠️ Pas de connexion, mise à jour locale uniquement')
    // 🆕 Retourner le match local corrigé (pas les données Supabase)
    return { success: true, offline: true, data: updatedFileData }
  }
  
  try {
    const matchData = prepareMatchForSupabase(match, true);
    
    const { error } = await supabase
      .from('matches')
      .update(matchData)
      .eq('id', match.id)
    
    if (error) throw error
    
    // Marquer comme synchronisé
    const updatedMatches = JSON.parse(localStorage.getItem('matches') || '[]')
    const updatedIndex = updatedMatches.findIndex(m => m.id === match.id)
    if (updatedIndex !== -1) {
      updatedMatches[updatedIndex].synced = true
      updatedMatches[updatedIndex].needsSync = false
      localStorage.setItem('matches', JSON.stringify(updatedMatches))
    }
    
    console.log('✅ Match mis à jour dans Supabase')
    return { success: true, online: true, data: updatedFileData }
    
  } catch (error) {
    console.error('❌ Erreur mise à jour Supabase:', error.message)
    return { success: true, offline: true, data: updatedFileData, error: error.message }
  }
}